// features/procurement/RequisitionDetailPage.tsx
/**
 * Purchase Requisition Detail — Redesigned Executive View
 * Features:
 * - Interactive multi-stage status timeline (Draft -> Pending -> Approved -> Converted to PO)
 * - Spring-animated Reject Modal with reason validation
 * - Spring-animated Convert to PO Modal with supplier selector
 * - Department & requester identity cards
 * - Zero silent failures with explicit notifications
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import {
    ArrowLeft, ClipboardList, Store, User, Calendar,
    CheckCircle2, Clock, Ban, FileText, Package,
    Building2, Sparkles, ChevronRight, X, AlertTriangle,
    Search, Layers
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import {
    getRequisition,
    approveRequisition,
    rejectRequisition,
    convertRequisitionToPO,
    getVendors,
    getProcurementErrorMessage,
    PurchaseRequisition,
    Vendor,
} from './services/procurementApi';

const FLOW_STEPS = ['draft', 'pending_approval', 'approved', 'converted_to_po'];

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; border: string; icon: any }> = {
    draft: { label: 'Draft Requisition', bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200', icon: Clock },
    pending_approval: { label: 'Awaiting Authorization', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: Clock },
    approved: { label: 'Approved (Ready for PO)', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: CheckCircle2 },
    rejected: { label: 'Declined', bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', icon: Ban },
    converted_to_po: { label: 'Converted to PO', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle2 },
};

export function RequisitionDetailPage() {
    const { reqId } = useParams<{ reqId: string }>();
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [showConvertModal, setShowConvertModal] = useState(false);
    const [selectedVendorId, setSelectedVendorId] = useState('');
    const [vendorSearch, setVendorSearch] = useState('');

    // ============================================================
    // QUERIES
    // ============================================================

    const { data: req, isLoading, isError, refetch } = useQuery<PurchaseRequisition>({
        queryKey: ['requisition-detail', reqId],
        queryFn: () => getRequisition(user!.tenantId, reqId!),
        enabled: !!user?.tenantId && !!reqId,
    });

    const { data: vendorsData, isLoading: isLoadingVendors } = useQuery<Vendor[]>({
        queryKey: ['vendors-convert', user?.tenantId],
        queryFn: () => getVendors(user!.tenantId, { limit: 100 }),
        enabled: !!user?.tenantId && showConvertModal,
    });

    const vendors = vendorsData || [];

    // ============================================================
    // MUTATIONS
    // ============================================================

    const approveMutation = useMutation({
        mutationFn: () => approveRequisition(user!.tenantId, reqId!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['requisition-detail', reqId] });
            queryClient.invalidateQueries({ queryKey: ['requisitions-list'] });
            addToast({
                type: 'success',
                title: 'Requisition Approved',
                message: `Requisition #${req?.requisitionNumber} authorized. You can now convert it to a Purchase Order.`,
            });
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Approval Failed',
                message: getProcurementErrorMessage(err),
            });
        },
    });

    const rejectMutation = useMutation({
        mutationFn: () => {
            if (!rejectReason.trim()) {
                throw new Error('Please enter an explicit reason for declining this requisition.');
            }
            return rejectRequisition(user!.tenantId, reqId!, rejectReason.trim());
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['requisition-detail', reqId] });
            queryClient.invalidateQueries({ queryKey: ['requisitions-list'] });
            setShowRejectModal(false);
            setRejectReason('');
            addToast({
                type: 'success',
                title: 'Requisition Declined',
                message: `Requisition #${req?.requisitionNumber} has been marked as rejected.`,
            });
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Rejection Failed',
                message: getProcurementErrorMessage(err, err?.message || 'Failed to reject requisition.'),
            });
        },
    });

    const convertMutation = useMutation({
        mutationFn: () => {
            if (!selectedVendorId) {
                throw new Error('Please select an authorized supplier before converting this requisition.');
            }
            return convertRequisitionToPO(user!.tenantId, reqId!, selectedVendorId);
        },
        onSuccess: (po) => {
            queryClient.invalidateQueries({ queryKey: ['requisition-detail', reqId] });
            queryClient.invalidateQueries({ queryKey: ['requisitions-list'] });
            queryClient.invalidateQueries({ queryKey: ['purchase-orders-list'] });
            setShowConvertModal(false);
            setSelectedVendorId('');
            addToast({
                type: 'success',
                title: 'Converted to Purchase Order',
                message: `Generated PO #${po.poNumber}. Redirecting to order detail...`,
            });
            navigate(`/procurement/orders/${po.id}`);
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'PO Conversion Failed',
                message: getProcurementErrorMessage(err, err?.message || 'Failed to convert requisition to purchase order.'),
            });
        },
    });

    // ============================================================
    // HELPERS
    // ============================================================

    const formatNaira = (val: string | number | null | undefined) => {
        if (!val) return '₦0.00';
        const num = typeof val === 'string' ? parseFloat(val) : val;
        return isNaN(num) ? '₦0.00' : `₦${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (d: string | null | undefined) => {
        if (!d) return '—';
        return new Date(d).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[70vh] bg-slate-50/60 text-slate-600 gap-4">
                <Spinner size="lg" variant="teal" />
                <p className="text-sm font-semibold tracking-wide text-slate-700">Loading requisition record...</p>
            </div>
        );
    }

    if (isError || !req) {
        return (
            <div className="min-h-screen bg-slate-50/60 flex items-center justify-center p-6">
                <ErrorState
                    title="Requisition Not Found"
                    message="The requested internal requisition document could not be loaded or has been deleted."
                    onBack={() => navigate('/procurement/requisitions')}
                    onRetry={() => refetch()}
                />
            </div>
        );
    }

    const currentStepIndex = FLOW_STEPS.indexOf(req.status);
    const statusCfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.draft;
    const StatusIcon = statusCfg.icon;

    const totalQty = req.items?.reduce((s, item) => s + parseFloat(item.quantity || '0'), 0) || 0;
    const totalEstCost = req.items?.reduce((s, item) => {
        const qty = parseFloat(item.quantity || '0');
        const cost = parseFloat(item.estimatedUnitCost || '0');
        return s + (qty * (isNaN(cost) ? 0 : cost));
    }, 0) || 0;

    const filteredVendors = vendors.filter(v =>
        !vendorSearch || v.name.toLowerCase().includes(vendorSearch.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-slate-50/60 text-slate-800 pb-20">
            {/* Top Action Bar */}
            <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/procurement/requisitions')}
                                className="p-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 border border-slate-200 transition-all shadow-sm"
                                type="button"
                                title="Back to Requisitions"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg shadow-blue-500/20 text-white">
                                <ClipboardList className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2.5 flex-wrap">
                                    <h1 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900 font-mono">
                                        #{req.requisitionNumber}
                                    </h1>
                                    <span className={clsx('px-3 py-1 rounded-full text-xs font-bold border flex items-center gap-1.5', statusCfg.bg, statusCfg.text, statusCfg.border)}>
                                        <StatusIcon className="w-3.5 h-3.5" />
                                        <span>{statusCfg.label}</span>
                                    </span>
                                </div>
                                <p className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                                    <span className="font-semibold text-slate-700">{req.requesterName || 'Requester'}</span>
                                    <span>•</span>
                                    <span>{req.branchName || 'Branch'}</span>
                                    <span>•</span>
                                    <span>Submitted {formatDate(req.createdAt)}</span>
                                </p>
                            </div>
                        </div>

                        {/* Top CTAs */}
                        <div className="flex items-center gap-3 self-end sm:self-auto flex-wrap">
                            {(req.status === 'draft' || req.status === 'pending_approval') && (
                                <>
                                    <button
                                        onClick={() => setShowRejectModal(true)}
                                        className="px-4 py-2.5 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 rounded-xl font-bold text-xs transition-all flex items-center gap-1.5 cursor-pointer"
                                        type="button"
                                    >
                                        <Ban className="w-4 h-4" />
                                        <span>Reject Indent</span>
                                    </button>
                                    <button
                                        onClick={() => approveMutation.mutate()}
                                        disabled={approveMutation.isPending}
                                        className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-xl font-bold text-xs shadow-lg shadow-emerald-500/20 transition-all cursor-pointer"
                                        type="button"
                                    >
                                        {approveMutation.isPending ? (
                                            <Spinner size="xs" variant="white" />
                                        ) : (
                                            <CheckCircle2 className="w-4 h-4" />
                                        )}
                                        <span>Authorize Requisition</span>
                                    </button>
                                </>
                            )}

                            {req.status === 'approved' && (
                                <button
                                    onClick={() => setShowConvertModal(true)}
                                    className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl font-bold text-xs shadow-lg shadow-blue-500/20 transition-all cursor-pointer"
                                    type="button"
                                >
                                    <FileText className="w-4 h-4" />
                                    <span>Convert to Purchase Order</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 space-y-8">
                {/* Status Timeline */}
                <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
                    <div className="flex items-center justify-between text-xs text-slate-500">
                        <span className="font-bold uppercase tracking-wider text-blue-600 flex items-center gap-1.5">
                            <Sparkles className="w-4 h-4" />
                            <span>Authorization Pipeline</span>
                        </span>
                        <span>{totalQty.toLocaleString()} units requested</span>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        {FLOW_STEPS.map((step, idx) => {
                            const isDone = currentStepIndex >= 0 && idx <= currentStepIndex;
                            const isCurrent = idx === currentStepIndex;

                            return (
                                <div key={step} className="flex items-center gap-2">
                                    <div className={clsx(
                                        'px-3.5 py-1.5 rounded-xl text-xs font-bold border transition-all flex items-center gap-2',
                                        isCurrent
                                            ? 'bg-blue-50 border-blue-300 text-blue-700 shadow-sm'
                                            : isDone
                                                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                                : 'bg-slate-50 border-slate-200 text-slate-400'
                                    )}>
                                        <div className={clsx(
                                            'w-2 h-2 rounded-full',
                                            isCurrent ? 'bg-blue-500 animate-pulse' : isDone ? 'bg-emerald-500' : 'bg-slate-300'
                                        )} />
                                        <span>{step.replace(/_/g, ' ').toUpperCase()}</span>
                                    </div>
                                    {idx < FLOW_STEPS.length - 1 && (
                                        <ChevronRight className={clsx('w-4 h-4', isDone ? 'text-blue-400' : 'text-slate-300')} />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Scorecards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-bold mb-1">
                            <Store className="w-3.5 h-3.5 text-blue-600" /> Requesting Branch
                        </div>
                        <p className="font-bold text-slate-900 text-sm">{req.branchName || 'Main Facility'}</p>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-bold mb-1">
                            <User className="w-3.5 h-3.5 text-teal-600" /> Staff Member
                        </div>
                        <p className="font-bold text-slate-900 text-sm">{req.requesterName || 'Staff Member'}</p>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-bold mb-1">
                            <Calendar className="w-3.5 h-3.5 text-amber-600" /> Date Logged
                        </div>
                        <p className="font-bold text-slate-900 text-sm">{formatDate(req.createdAt)}</p>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-bold mb-1">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Approval Status
                        </div>
                        <p className="font-bold text-slate-900 text-sm">
                            {req.approvedAt ? `Approved ${formatDate(req.approvedAt)}` : 'Pending Manager Review'}
                        </p>
                    </div>
                </div>

                {/* Requisition Items Table */}
                <div className="bg-white border border-slate-200 rounded-3xl p-6 lg:p-8 shadow-sm space-y-6">
                    <div className="flex items-center justify-between pb-4 border-b border-slate-200">
                        <div>
                            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                <Package className="w-5 h-5 text-blue-600" />
                                <span>Requested Materials ({req.items?.length || 0})</span>
                            </h3>
                            <p className="text-xs text-slate-500">Inventory lines requested for restocking and fulfillment</p>
                        </div>
                        {req.status === 'approved' && (
                            <button
                                onClick={() => setShowConvertModal(true)}
                                className="px-4 py-2 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
                                type="button"
                            >
                                <FileText className="w-3.5 h-3.5" />
                                <span>Create PO From This Indent</span>
                            </button>
                        )}
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50/50">
                                    <th className="py-3 px-4">Product Catalog</th>
                                    <th className="py-3 px-4 text-right">Quantity Requested</th>
                                    <th className="py-3 px-4 text-right">Estimated Unit Rate</th>
                                    <th className="py-3 px-4 text-right">Estimated Budget</th>
                                    <th className="py-3 px-4">Internal Notes</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {req.items?.map((item) => {
                                    const qty = parseFloat(item.quantity || '0');
                                    const cost = parseFloat(item.estimatedUnitCost || '0');
                                    const total = qty * cost;

                                    return (
                                        <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                                            <td className="py-4 px-4">
                                                <p className="font-bold text-slate-900 text-sm">{item.productName || 'Inventory Product'}</p>
                                                <p className="text-xs text-slate-500 font-mono">UOM: {item.uomName || 'Unit'}</p>
                                            </td>
                                            <td className="py-4 px-4 text-right font-bold text-slate-900">
                                                {qty.toLocaleString()}
                                            </td>
                                            <td className="py-4 px-4 text-right font-mono text-slate-600">
                                                {formatNaira(item.estimatedUnitCost)}
                                            </td>
                                            <td className="py-4 px-4 text-right font-bold text-blue-600 font-mono">
                                                {formatNaira(total)}
                                            </td>
                                            <td className="py-4 px-4 text-xs text-slate-500">
                                                {item.notes || '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Financial Summary */}
                    <div className="pt-6 border-t border-slate-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                        <div className="text-xs text-slate-500 space-y-2 max-w-md">
                            {req.notes && (
                                <p className="text-slate-700">
                                    <span className="font-bold text-slate-900">Justification: </span>
                                    {req.notes}
                                </p>
                            )}
                            <p className="text-[11px] text-slate-400">
                                Unit rates are estimates based on standard catalog costs. Exact totals will be confirmed upon vendor PO issuance.
                            </p>
                        </div>

                        <div className="w-full md:w-80 bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-2.5">
                            <div className="flex items-center justify-between text-xs text-slate-600">
                                <span>Total Units Requested</span>
                                <span className="font-semibold text-slate-900">{totalQty.toLocaleString()} units</span>
                            </div>
                            <div className="pt-3 border-t border-slate-200 flex items-center justify-between">
                                <span className="text-sm font-bold text-slate-900">Estimated Budget</span>
                                <span className="text-base font-black text-blue-600">{formatNaira(totalEstCost)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ========== REJECT MODAL ========== */}
            <AnimatePresence>
                {showRejectModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                        onClick={() => setShowRejectModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white border border-slate-200 rounded-3xl shadow-2xl w-full max-w-md p-6 sm:p-8 space-y-5"
                        >
                            <div className="w-16 h-16 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
                                <Ban className="w-8 h-8" />
                            </div>

                            <div className="text-center">
                                <h2 className="text-xl font-bold text-slate-900">Decline Requisition?</h2>
                                <p className="text-xs text-slate-500 mt-1">
                                    Please specify why Requisition #{req.requisitionNumber} is being declined.
                                </p>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                                    Rejection Reason <span className="text-rose-500">*</span>
                                </label>
                                <textarea
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    rows={3}
                                    placeholder="e.g. Budget ceiling exceeded, redundant stock already in central warehouse, unapproved items..."
                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-rose-500 focus:ring-1 focus:ring-rose-500 resize-none placeholder:text-slate-400"
                                    autoFocus
                                />
                            </div>

                            <div className="flex items-center gap-3 pt-2">
                                <button
                                    onClick={() => setShowRejectModal(false)}
                                    className="flex-1 px-4 py-3 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold text-sm transition-all"
                                    type="button"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => rejectMutation.mutate()}
                                    disabled={rejectMutation.isPending || !rejectReason.trim()}
                                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-rose-600 to-pink-600 text-white hover:from-rose-500 hover:to-pink-500 font-bold text-sm shadow-xl shadow-rose-500/20 transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2"
                                    type="button"
                                >
                                    {rejectMutation.isPending ? (
                                        <Spinner size="xs" variant="white" />
                                    ) : (
                                        'Decline Indent'
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ========== CONVERT TO PO MODAL ========== */}
            <AnimatePresence>
                {showConvertModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                        onClick={() => setShowConvertModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white border border-slate-200 rounded-3xl shadow-2xl w-full max-w-lg p-6 sm:p-8 space-y-6"
                        >
                            <div className="flex items-center justify-between pb-3 border-b border-slate-200">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 rounded-2xl bg-blue-50 text-blue-600">
                                        <FileText className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-bold text-slate-900">Convert to Purchase Order</h2>
                                        <p className="text-xs text-slate-500">Select supplier to generate contract order for #{req.requisitionNumber}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowConvertModal(false)}
                                    className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                                    type="button"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Vendor Selector */}
                            <div className="space-y-3">
                                <div className="relative">
                                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="Filter supplier by name..."
                                        value={vendorSearch}
                                        onChange={(e) => setVendorSearch(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:border-blue-500"
                                    />
                                </div>

                                <div className="max-h-56 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-2xl p-1 bg-slate-50/50">
                                    {filteredVendors.length === 0 ? (
                                        <div className="p-4 text-center text-xs text-slate-400">
                                            No matching vendors found.
                                        </div>
                                    ) : (
                                        filteredVendors.map((v) => (
                                            <button
                                                key={v.id}
                                                type="button"
                                                onClick={() => setSelectedVendorId(v.id)}
                                                className={clsx(
                                                    'w-full text-left p-3 rounded-xl transition-all flex items-center justify-between',
                                                    selectedVendorId === v.id
                                                        ? 'bg-blue-50 border border-blue-200 text-blue-900'
                                                        : 'hover:bg-white text-slate-700'
                                                )}
                                            >
                                                <div>
                                                    <p className="font-bold text-sm text-slate-900">{v.name}</p>
                                                    <p className="text-[11px] text-slate-500">
                                                        Terms: {v.paymentTerms || 'Net 30'} · Lead Time: {v.leadTimeDays || 7}d
                                                    </p>
                                                </div>
                                                {selectedVendorId === v.id && (
                                                    <CheckCircle2 className="w-5 h-5 text-blue-600" />
                                                )}
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center gap-3 pt-2">
                                <button
                                    onClick={() => setShowConvertModal(false)}
                                    className="flex-1 px-4 py-3 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold text-sm transition-all"
                                    type="button"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => convertMutation.mutate()}
                                    disabled={convertMutation.isPending || !selectedVendorId}
                                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-500 hover:to-indigo-500 font-bold text-sm shadow-xl shadow-blue-500/20 transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2"
                                    type="button"
                                >
                                    {convertMutation.isPending ? (
                                        <Spinner size="xs" variant="white" />
                                    ) : (
                                        'Create Purchase Order'
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}